const FLOATING_ELEMENT_SELECTOR = '.js-floating-offset, [data-floating-offset], [data-floating-variable], [data-floating-target], [data-floating-active-class], [data-floating-active-target], [data-floating-element-variable], [data-floating-compensate-property], [data-floating-compensate-target], [data-floating-compensate-template]';

let floatingOffsetRaf = null;
let floatingOffsetResizeTimer = null;
let floatingOffsetPendingReason = null;
let floatingOffsetPendingMutation = {added: false, removed: false};
const registeredFloatingElements = new Set();
const floatingOffsetConfigCache = new Map();

const FLOATING_OFFSET_REASON_PRIORITY = {
    mutation: 1,
    initial: 2,
    resize: 2
};

function parseFloatingOffsetDescriptor(element, fallbackRatio = 0.5) {
    if (!element) {
        return { type: 'ratio', value: fallbackRatio };
    }

    const raw = element.getAttribute('data-floating-offset');
    if (!raw) {
        return { type: 'ratio', value: fallbackRatio };
    }

    const trimmed = raw.trim();
    if (!trimmed) {
        return { type: 'ratio', value: fallbackRatio };
    }

    if (trimmed.endsWith('px')) {
        const value = parseFloat(trimmed.slice(0, -2));
        return { type: 'pixel', value: Number.isFinite(value) ? value : 0 };
    }

    if (trimmed.endsWith('%')) {
        const value = parseFloat(trimmed.slice(0, -1));
        return { type: 'ratio', value: Number.isFinite(value) ? value / 100 : fallbackRatio };
    }

    const numeric = parseFloat(trimmed);
    if (!Number.isFinite(numeric)) {
        return { type: 'ratio', value: fallbackRatio };
    }

    return { type: 'ratio', value: numeric };
}

function resolveFloatingTarget(element, targetAttr) {
    if (!targetAttr) {
        return null;
    }

    if (targetAttr === 'self') {
        return element;
    }

    if (targetAttr.startsWith('closest:')) {
        return element.closest(targetAttr.slice('closest:'.length).trim());
    }

    try {
        return document.querySelector(targetAttr);
    } catch (error) {
        console.warn('[floating-offset] invalid selector', targetAttr, error);
        return null;
    }
}

function computeFloatingOffset(element, descriptor) {
    if (!element) {
        return 0;
    }

    const effectiveDescriptor = descriptor || { type: 'ratio', value: 0.5 };

    if (effectiveDescriptor.type === 'pixel') {
        return Math.max(0, Math.round(effectiveDescriptor.value || 0));
    }

    const ratio = Number.isFinite(effectiveDescriptor.value) ? effectiveDescriptor.value : 0.5;
    const height = element.offsetHeight || 0;
    return Math.max(0, Math.round(height * ratio));
}

function registerFloatingElement(element) {
    if (!element || registeredFloatingElements.has(element)) {
        return false;
    }

    registeredFloatingElements.add(element);
    return true;
}

function unregisterFloatingElement(element) {
    if (!element) {
        return false;
    }

    const wasRegistered = registeredFloatingElements.delete(element);
    if (!wasRegistered) {
        return false;
    }

    floatingOffsetConfigCache.delete(element);
    return true;
}

function collectFloatingElements(root = document) {
    if (!root) {
        return false;
    }

    let registered = false;

    if (root.matches?.(FLOATING_ELEMENT_SELECTOR)) {
        registered = registerFloatingElement(root) || registered;
    }

    root.querySelectorAll?.(FLOATING_ELEMENT_SELECTOR).forEach((element) => {
        if (registerFloatingElement(element)) {
            registered = true;
        }
    });

    return registered;
}

function unregisterFloatingElements(root) {
    if (!root) {
        return false;
    }

    let removed = false;

    if (root.matches?.(FLOATING_ELEMENT_SELECTOR)) {
        removed = unregisterFloatingElement(root) || removed;
    }

    root.querySelectorAll?.(FLOATING_ELEMENT_SELECTOR).forEach((element) => {
        if (unregisterFloatingElement(element)) {
            removed = true;
        }
    });

    return removed;
}

function getOriginalMarginTop(article) {
    if (!article) {
        return '0px';
    }

    const previousInline = article.style.marginTop;
    article.style.marginTop = '';
    const computed = window.getComputedStyle(article).marginTop;
    article.style.marginTop = previousInline;
    return computed || '0px';
}

function applyArticleMarginAdjustment(article, offset) {
    if (!article) {
        return;
    }

    const originalValue = getOriginalMarginTop(article);
    const original = parseFloat(originalValue) || 0;
    if (offset > 0) {
        const newMargin = Math.max(0, original - offset);
        article.style.marginTop = `${newMargin}px`;
    } else {
        article.style.marginTop = '';
    }
}

function findDefaultCompensationTarget(element) {
    if (!element || !element.parentElement) {
        return null;
    }

    const isInFooter = element.closest('#footer');
    if (isInFooter) {
        const mainArticles = document.querySelectorAll('#container .mod_article');
        if (mainArticles.length) {
            return mainArticles[mainArticles.length - 1];
        }
    }

    const article = element.closest('.mod_article');
    if (article) {
        let previousArticle = article.previousElementSibling;
        while (previousArticle) {
            if (previousArticle.matches('.mod_article')) {
                return previousArticle;
            }
            previousArticle = previousArticle.previousElementSibling;
        }
    }

    let current = element;
    while (current) {
        let sibling = current.previousElementSibling;
        while (sibling) {
            if (sibling.classList && sibling.classList.contains('content--element')) {
                return sibling;
            }
            sibling = sibling.previousElementSibling;
        }

        current = current.parentElement;
    }

    return null;
}

function buildFloatingConfigFromElement(element) {
    const offsetDescriptor = parseFloatingOffsetDescriptor(element);
    const resolvedOffset = computeFloatingOffset(element, offsetDescriptor);
    const variableName = element.getAttribute('data-floating-variable') || '--floating-offset';
    const targetAttr = element.getAttribute('data-floating-target');
    const variableTarget = resolveFloatingTarget(element, targetAttr) || element;

    const toggleClassName = element.getAttribute('data-floating-active-class');
    const toggleTargetAttr = element.getAttribute('data-floating-active-target') || targetAttr;
    const toggleClassTarget = toggleClassName ? (resolveFloatingTarget(element, toggleTargetAttr) || variableTarget) : null;

    const elementVariableName = element.getAttribute('data-floating-element-variable') || '--floating-offset';

    let compensatePropertyRaw = element.getAttribute('data-floating-compensate-property');
    let compensateProperty = compensatePropertyRaw ? compensatePropertyRaw.trim() : '';
    const compensateTargetAttr = element.getAttribute('data-floating-compensate-target');
    let compensateTemplateRaw = element.getAttribute('data-floating-compensate-template');
    const animateAttr = element.getAttribute('data-floating-animate');
    const hasAnimateAttr = animateAttr !== null;
    const prefersReducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const animate = !prefersReducedMotion && (
        hasAnimateAttr
            ? !/^(false|off|0|no)$/i.test(animateAttr.trim())
            : true
    );

    const adjustArticleMargin = element.hasAttribute('data-floating-adjust-margin');
    const articleMarginTarget = adjustArticleMargin ? element.closest('.mod_article') : null;

    const disableCompensation = compensateProperty && /^(none|off|false|0)$/i.test(compensateProperty);
    if (disableCompensation) {
        compensateProperty = '';
    }

    if (!compensateProperty && !disableCompensation) {
        compensateProperty = 'padding-bottom';
    }

    let compensateTemplate = compensateTemplateRaw && compensateTemplateRaw.trim()
        ? compensateTemplateRaw.trim()
        : '';

    if (!compensateTemplate && !disableCompensation) {
        compensateTemplate = '{{offset}}';
    }

    let compensateTarget = null;
    if (compensateProperty) {
        if (compensateTargetAttr) {
            compensateTarget = resolveFloatingTarget(element, compensateTargetAttr);
        } else {
            compensateTarget = findDefaultCompensationTarget(element);
        }
    }
    const finalCompensateTemplate = compensateTemplate || '{{offset}}';

    return {
        element,
        variableTarget,
        variableName,
        toggleClassTarget,
        toggleClassName,
        elementVariableName,
        offsetDescriptor,
        resolvedOffset,
        compensateTarget,
        compensateProperty,
        compensateTemplate: finalCompensateTemplate,
        animate,
        animateExplicit: hasAnimateAttr,
        articleMarginTarget,
        adjustArticleMargin
    };
}

function applyFloatingConfig(config, articleOffsetMap) {
    const {
        element,
        variableTarget,
        variableName,
        toggleClassTarget,
        toggleClassName,
        elementVariableName,
        resolvedOffset,
        compensateTarget,
        compensateProperty,
        compensateTemplate,
        animate,
        animateExplicit,
        articleMarginTarget,
        adjustArticleMargin
    } = config;

    if (!variableTarget || !variableName) {
        return;
    }

    const offset = resolvedOffset;
    variableTarget.style.setProperty(variableName, `${offset}px`);

    if (toggleClassTarget && toggleClassName) {
        toggleClassTarget.classList.toggle(toggleClassName, offset > 0);
    }

    if (element && elementVariableName) {
        element.style.setProperty(elementVariableName, `${offset}px`);
    }

    if (element) {
        if (animate) {
            element.classList.add('floating-offset--animated');
        } else if (animateExplicit) {
            element.classList.remove('floating-offset--animated');
        }
    }

    if (articleMarginTarget && adjustArticleMargin) {
        const articleOffset = articleOffsetMap.get(articleMarginTarget) || offset;
        applyArticleMarginAdjustment(articleMarginTarget, articleOffset);
    }

    if (compensateTarget && compensateProperty) {
        const template = compensateTemplate || '{{offset}}';
        const propertyValue = template.replace(/\{\{\s*offset\s*\}\}/gi, `${offset}px`);
        compensateTarget.style.setProperty(compensateProperty, propertyValue);
    }
}

function applyFloatingOffsets(reason = 'mutation', mutationDetail = {added: false, removed: false}) {
    const activeElements = [];
    registeredFloatingElements.forEach((element) => {
        if (element.isConnected) {
            activeElements.push(element);
        } else {
            unregisterFloatingElement(element);
        }
    });

    if (activeElements.length === 0) {
        return;
    }

    const isFullRecalculation = reason === 'resize' || reason === 'initial';
    const configsToApply = [];
    const configsToApplySet = new Set();
    const addConfigToApply = (config) => {
        if (configsToApplySet.has(config)) {
            return;
        }
        configsToApplySet.add(config);
        configsToApply.push(config);
    };

    if (isFullRecalculation) {
        floatingOffsetConfigCache.clear();
        activeElements.forEach((element) => {
            const config = buildFloatingConfigFromElement(element);
            floatingOffsetConfigCache.set(element, config);
            addConfigToApply(config);
        });
    } else {
        floatingOffsetConfigCache.forEach((_, element) => {
            if (!registeredFloatingElements.has(element)) {
                floatingOffsetConfigCache.delete(element);
            }
        });

        const hasAdditions = Boolean(mutationDetail?.added);
        const hasRemovals = Boolean(mutationDetail?.removed);

        if (!hasAdditions && !hasRemovals) {
            return;
        }

        if (hasAdditions) {
            activeElements.forEach((element) => {
                if (!floatingOffsetConfigCache.has(element)) {
                    const config = buildFloatingConfigFromElement(element);
                    floatingOffsetConfigCache.set(element, config);
                    addConfigToApply(config);
                }
            });
        }

        if (hasRemovals) {
            floatingOffsetConfigCache.forEach((config) => {
                addConfigToApply(config);
            });
        }
    }

    if (configsToApply.length === 0) {
        return;
    }

    const articleOffsetMap = new Map();
    floatingOffsetConfigCache.forEach((config) => {
        if (!config.articleMarginTarget || !config.adjustArticleMargin) {
            return;
        }
        const current = articleOffsetMap.get(config.articleMarginTarget) || 0;
        if (config.resolvedOffset > current) {
            articleOffsetMap.set(config.articleMarginTarget, config.resolvedOffset);
        }
    });

    configsToApply.forEach((config) => {
        applyFloatingConfig(config, articleOffsetMap);
    });
}

function getFloatingOffsetReasonPriority(reason) {
    return FLOATING_OFFSET_REASON_PRIORITY[reason] ?? FLOATING_OFFSET_REASON_PRIORITY.mutation;
}

export function scheduleFloatingOffsetUpdate(reason = 'mutation', mutationDetail = {added: false, removed: false}) {
    if (registeredFloatingElements.size === 0) {
        return;
    }

    if (reason === 'mutation') {
        if (mutationDetail?.added) {
            floatingOffsetPendingMutation.added = true;
        }
        if (mutationDetail?.removed) {
            floatingOffsetPendingMutation.removed = true;
        }
    } else {
        floatingOffsetPendingMutation = {added: false, removed: false};
    }

    const incomingPriority = getFloatingOffsetReasonPriority(reason);
    const currentPriority = floatingOffsetPendingReason
        ? getFloatingOffsetReasonPriority(floatingOffsetPendingReason)
        : -1;

    if (!floatingOffsetPendingReason || incomingPriority >= currentPriority) {
        floatingOffsetPendingReason = reason;
    }

    if (floatingOffsetRaf) {
        cancelAnimationFrame(floatingOffsetRaf);
    }

    floatingOffsetRaf = requestAnimationFrame(() => {
        floatingOffsetRaf = null;
        const reasonToApply = floatingOffsetPendingReason || 'mutation';
        const mutationInfo = floatingOffsetPendingMutation;
        floatingOffsetPendingReason = null;
        floatingOffsetPendingMutation = {added: false, removed: false};
        applyFloatingOffsets(reasonToApply, mutationInfo);
    });
}

export function initFloatingOffsetHandling() {
    const handleContentReady = () => {
        collectFloatingElements(document);
        scheduleFloatingOffsetUpdate('initial');
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', handleContentReady, {once: true});
    } else {
        handleContentReady();
    }

    window.addEventListener('load', () => {
        collectFloatingElements(document);
        scheduleFloatingOffsetUpdate('initial');
    });

    window.addEventListener('orientationchange', () => {
        scheduleFloatingOffsetUpdate('resize');
    }, {passive: true});
    window.addEventListener('resize', () => {
        clearTimeout(floatingOffsetResizeTimer);
        floatingOffsetResizeTimer = setTimeout(() => {
            scheduleFloatingOffsetUpdate('resize');
        }, 150);
    }, {passive: true});

    if ('MutationObserver' in window && document.body) {
        const observer = new MutationObserver((mutations) => {
            let needsUpdate = false;
            let mutationAdded = false;
            let mutationRemoved = false;

            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    if (node.nodeType !== 1) {
                        return;
                    }

                    if (collectFloatingElements(node)) {
                        needsUpdate = true;
                        mutationAdded = true;
                    }
                });

                mutation.removedNodes.forEach((node) => {
                    if (node.nodeType !== 1) {
                        return;
                    }

                    if (unregisterFloatingElements(node)) {
                        needsUpdate = true;
                        mutationRemoved = true;
                    }
                });
            });

            if (needsUpdate) {
                scheduleFloatingOffsetUpdate('mutation', {
                    added: mutationAdded,
                    removed: mutationRemoved
                });
            }
        });

        observer.observe(document.body, {childList: true, subtree: true});
    }
}
